import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type MouseEvent,
    type KeyboardEvent,
} from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { CellType, type BaseCellProps } from "@components/spreadsheet/cells";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import type { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { buildFlatItems, plainFlatItemOptions } from "./PickerPlayground.lib";
import styles from "./PickerPlayground.module.scss";

const stopPropagation = (event: MouseEvent | KeyboardEvent) =>
    event.stopPropagation();

/**
 * The spreadsheet edit-mode shim for a Picker cell: the grid owns edit mode
 * (Enter / typing enters it, Escape and a pick leave it), so while editing
 * the cell mounts an open `trigger="cellInput"` root, seeds its text with the
 * key that opened it (as `DropdownCell` does with `initialSearch`), focuses
 * the input once that keystroke has dispatched, commits the pick through
 * `finishEditing(value)` and hands control back on close. This is what S2
 * carries into `DropdownCell`.
 */
const PickerSpreadsheetCell = ({
    value,
    isEditing,
    finishEditing,
    enterEditModeKey,
    items,
}: Pick<
    BaseCellProps<string>,
    "value" | "isEditing" | "finishEditing" | "enterEditModeKey"
> & { items: Array<PickerItemData<string>> }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    // Focus after the opening keystroke has finished dispatching, so the
    // seeded text is not doubled by the same key landing in the input.
    useEffect(() => {
        if (!isEditing) {
            return;
        }
        const frame = requestAnimationFrame(() => inputRef.current?.focus());
        return () => cancelAnimationFrame(frame);
    }, [isEditing]);
    const label = items.find((item) => item.value === value)?.label ?? "";

    if (!isEditing) {
        return <div className={styles.spreadsheetDisplay}>{label}</div>;
    }
    return (
        <div
            className={styles.spreadsheetEditor}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onKeyDown={stopPropagation}
        >
            <Picker.Select
                items={items}
                trigger="cellInput"
                ref={inputRef}
                value={value}
                defaultOpen
                // Like DropdownCell's `initialSearch`: the key that opened edit
                // mode seeds the text (the grid does not replay it); Enter /
                // double-click start from the current label.
                defaultInputValue={
                    enterEditModeKey?.length === 1 ? enterEditModeKey : label
                }
                onValueChange={(next) => finishEditing(next)}
                onOpenChange={(open) => {
                    if (!open) {
                        finishEditing();
                    }
                }}
            />
        </div>
    );
};

const initialData: SpreadsheetData = [
    { name: "Office rent", account: null, amount: 2400 },
    { name: "Cloud hosting", account: null, amount: 310 },
    { name: "Team lunch", account: null, amount: 86 },
];

/**
 * A real `SpreadsheetGrid` with a Picker cell editor in the middle column:
 * select a cell and press Enter or start typing to edit, Escape to return to
 * the grid.
 */
export const PickerSpreadsheetExample = () => {
    const items = useMemo(() => buildFlatItems(plainFlatItemOptions), []);
    const [data, setData] = useState(initialData);
    const columns = useMemo<ColumnDef[]>(
        () => [
            { field: "name", label: "Description", cellType: CellType.STRING },
            {
                field: "account",
                label: "Account (Picker)",
                cellFn: ({
                    value,
                    isEditing,
                    finishEditing,
                    enterEditModeKey,
                }) => (
                    <PickerSpreadsheetCell
                        items={items}
                        value={typeof value === "string" ? value : null}
                        isEditing={isEditing}
                        finishEditing={finishEditing}
                        enterEditModeKey={enterEditModeKey}
                    />
                ),
            },
            {
                field: "amount",
                label: "Amount",
                cellType: CellType.NUMBER,
                isMonetary: true,
            },
        ],
        [items],
    );

    return (
        <div className={styles.tableHost}>
            <SpreadsheetGrid
                data={data}
                columns={columns}
                onChange={(next) => setData(next)}
                showDropdownIndicator
                noOuterBorder={false}
            />
        </div>
    );
};
